import React, { useState, useMemo } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search } from 'lucide-react';

interface Channel {
  id: number;
  name: string;
}

interface ChannelSelectorProps {
  availableChannels: Channel[];
  selectedChannelIds: number[];
  onSelectionChange: (channelIds: number[]) => void;
  isLoading?: boolean;
  maxHeight?: string;
}

export const ChannelSelector: React.FC<ChannelSelectorProps> = ({
  availableChannels,
  selectedChannelIds,
  onSelectionChange,
  isLoading = false,
  maxHeight = 'max-h-96',
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const handleChannelToggle = (channelId: number) => {
    const newSelection = selectedChannelIds.includes(channelId)
      ? selectedChannelIds.filter((id) => id !== channelId)
      : [...selectedChannelIds, channelId];
    onSelectionChange(newSelection);
  };

  const selectedChannels = useMemo(
    () => availableChannels.filter((ch) => selectedChannelIds.includes(ch.id)),
    [availableChannels, selectedChannelIds]
  );

  const unselectedChannels = useMemo(
    () =>
      availableChannels
        .filter((ch) => !selectedChannelIds.includes(ch.id))
        .filter((ch) =>
          ch.name.toLowerCase().includes(searchTerm.toLowerCase())
        ),
    [availableChannels, selectedChannelIds, searchTerm]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" />
      </div>
    );
  }

  if (availableChannels.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-center">
        <p className="text-sm text-gray-500">No channels available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Selected Channels */}
      {selectedChannels.length > 0 && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
          <p className="mb-2 text-sm font-semibold text-orange-900">Selected Channels</p>
          <ScrollArea className="max-h-48">
            <div className="space-y-2 pr-4">
              {selectedChannels.map((ch) => (
                <div key={ch.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`selected-ch-${ch.id}`}
                    checked={true}
                    onCheckedChange={() => handleChannelToggle(ch.id)}
                  />
                  <Label
                    htmlFor={`selected-ch-${ch.id}`}
                    className="flex-1 cursor-pointer font-medium text-orange-900"
                  >
                    {ch.name}
                  </Label>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Search */}
      {availableChannels.length > 10 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search channels..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      {/* Available Channels */}
      {unselectedChannels.length > 0 ? (
        <div>
          <p className="mb-2 text-sm font-semibold text-gray-700">
            Available Channels
            {searchTerm && ` (${unselectedChannels.length} found)`}
          </p>
          <ScrollArea className={`rounded-lg border p-2 ${maxHeight}`}>
            <div className="space-y-2 pr-4">
              {unselectedChannels.map((ch) => (
                <div key={ch.id} className="flex items-center gap-2 rounded-lg p-2 hover:bg-gray-50">
                  <Checkbox
                    id={`ch-${ch.id}`}
                    checked={false}
                    onCheckedChange={() => handleChannelToggle(ch.id)}
                  />
                  <Label htmlFor={`ch-${ch.id}`} className="flex-1 cursor-pointer text-sm">
                    {ch.name}
                  </Label>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      ) : searchTerm ? (
        <div className="rounded-lg border border-dashed p-4 text-center">
          <p className="text-sm text-gray-500">No channels match your search</p>
        </div>
      ) : null}

      {/* Summary */}
      {selectedChannels.length > 0 && (
        <div className="border-t pt-3">
          <Badge variant="outline" className="bg-orange-100 text-orange-800">
            {selectedChannels.length} channel{selectedChannels.length !== 1 ? 's' : ''} selected
          </Badge>
        </div>
      )}
    </div>
  );
};

export default ChannelSelector;
